/**
 * Redact Document OCR lifecycle for the document editor.
 *
 * What OCR means, not when to look: this module reads a status into an
 * `ocrEngineState`, loads the manifest that says where each page's text came
 * from, and offers the user a reload once OCR has published a searchable copy
 * AS THE TDO'S PRIMARY ASSET — a run that left the original primary has no new
 * file to reload into, and offering one re-opened the same file and re-raised
 * the offer forever (VE-29505; see `settleOverUnpinnedFile`). The reading is
 * driven by the shared engine-status poller (`sagas/engineStatus.ts`), which
 * owns the fetch and the clock for every engine in the document redaction job.
 * The one saga owned here is `RELOAD_FOR_OCR`.
 *
 * Nothing gates the open. A document opens against whichever PDF is on the TDO;
 * where that copy has no text layer, search says so per page rather than the
 * file being withheld. The session keeps rendering the file it opened until the
 * reviewer takes the reload, which flushes unsaved work and re-opens.
 *
 * THAT RELOAD REACHES OUTSIDE THIS MODULE, which a reader here would not
 * otherwise expect. It re-resolves the document onto OCR's searchable copy, and
 * every lexicon evaluated against the old one is stale by that fact — so the
 * detections panel raises an "updating matches" notice on `RELOAD_FOR_OCR` and
 * the lexicon saga re-runs them once text arrives. When the pre-reload save
 * FAILS and nothing re-opens, this saga has to lower that notice itself
 * (`OCR_RELOAD_SETTLED`); nothing else can tell that the reload never happened.
 *
 * Task status, not `isOcrScanned`, is what this keys off. The flag is written
 * once at the end of a successful run, so its absence covers both "still
 * running" and "never ran".
 */
import {
  call,
  put,
  race,
  select,
  take,
  takeLatest,
} from 'typed-redux-saga/macro';

import { TDOId } from '@common-modules/universal/models/Brands';
import { enqueueSnackbar } from '@common-modules/snackbar';

import {
  OCR_FAILED,
  OCR_INDETERMINATE,
  OCR_MANIFEST_LOADED,
  OCR_NOT_RUN,
  OCR_PROCESSING,
  OCR_REFRESH_AVAILABLE,
  OCR_RELOAD_SETTLED,
  OCR_TIMED_OUT,
  OPEN_DOCUMENT,
  RELOAD_FOR_OCR,
  RUN_OCR_DETECTION,
  RUN_OCR_DETECTION_FAILURE,
  RUN_OCR_DETECTION_SUCCESS,
  SAVE_FAILURE,
  SAVE_NOW,
  SAVE_SUCCESS,
} from '../actions';
import { hasUnsavedWork, OcrManifest } from '../models';
import { selectSession } from '../selectors';
import {
  EngineTaskStatus,
  serviceCreateDocumentCognitionJob,
  serviceDownloadOcrManifest,
  serviceFetchPrimaryMediaAssetId,
  serviceFindOcrManifestUrl,
} from '../services';
import { selectGraphQLConfig, selectToken } from './auth';
import { selectConfigEngines } from '@common-modules/engines/selectors';

/**
 * Find and download the manifest without storing it; null when there is none.
 * The page count lets the service reject a manifest for a different file.
 */
export function* fetchOcrManifest(tdoId: TDOId, ocrEngineId: string) {
  const token = yield* select(selectToken);
  const { apiRoot, graphQLEndpoint, veritoneAppId } =
    yield* select(selectGraphQLConfig);

  const assetUrl = yield* call(
    serviceFindOcrManifestUrl,
    tdoId,
    ocrEngineId,
    token,
    apiRoot,
    graphQLEndpoint,
    veritoneAppId
  );
  if (!assetUrl) {
    return null;
  }

  const session = yield* select(selectSession);
  // `totalPages` is seeded at 1 and corrected by react-pdf once the document
  // parses, so at this point it is usually not yet trustworthy. Pass it only
  // when it looks real, and let the manifest's own pageCount stand otherwise —
  // a cross-check against a seed value would reject every manifest.
  const totalPages = session?.totalPages;
  const pdfPageCount =
    typeof totalPages === 'number' && totalPages > 1 ? totalPages : undefined;

  const manifest = yield* call(
    serviceDownloadOcrManifest,
    assetUrl,
    pdfPageCount
  );
  return manifest ?? null;
}

/** Find, download and store the manifest. Returns true when one was loaded. */
export function* loadOcrManifest(tdoId: TDOId, ocrEngineId: string) {
  const manifest = yield* fetchOcrManifest(tdoId, ocrEngineId);
  if (!manifest) {
    return false;
  }

  yield* put(OCR_MANIFEST_LOADED({ tdoId, manifest }));
  return true;
}

/**
 * Whether OCR wrote text onto any page. Only `ocr` pages add text; the other
 * sources are equally true of the original file.
 */
export const manifestClaimsOcrText = (manifest: OcrManifest): boolean =>
  manifest.pages.some((p) => p.source === 'ocr');

/**
 * Whether the TDO's primary `media` asset differs from the one this session
 * opened, i.e. OCR left a new file to reload into (VE-29505).
 *
 * A failed LOOKUP answers true, keeping the bias towards an unnecessary reload
 * over a false "searchable". A session that never resolved an asset at all is a
 * different question and never reaches here — see `settleOverUnpinnedFile`.
 */
export function* ocrPublishedNewFile(tdoId: TDOId, openedMediaAssetId: string) {
  try {
    const token = yield* select(selectToken);
    const { apiRoot, graphQLEndpoint, veritoneAppId } =
      yield* select(selectGraphQLConfig);
    const primaryId = yield* call(
      serviceFetchPrimaryMediaAssetId,
      tdoId,
      token,
      apiRoot,
      graphQLEndpoint,
      veritoneAppId
    );
    return primaryId !== openedMediaAssetId;
  } catch (e: unknown) {
    console.warn('OCR: could not resolve the primary media asset', e);
    return true;
  }
}

/**
 * What a status snapshot means for OCR. Classification only — callers decide
 * what to do with it, so the first reading and later ones cannot drift.
 */
export type OcrDisposition =
  /** Created 12h ago and still not finished. Nothing is coming. */
  | 'stalled'
  /** A task is in flight; OCR is reading the pages now. */
  | 'running'
  /** A task reached a terminal state — the manifest is the next question. */
  | 'settled'
  /** No OCR task found yet, and discovery is still looking. Assert NOTHING. */
  | 'awaiting-task'
  /** Nothing ran and nothing is coming. */
  | 'not-run';

export function classifyOcrStatus({
  hasRunning,
  hasComplete,
  hasFailed,
  hasStalled,
  mayStillAppear,
}: EngineTaskStatus): OcrDisposition {
  if (hasStalled) {
    return 'stalled';
  }
  if (hasRunning) {
    return 'running';
  }
  if (hasComplete || hasFailed) {
    return 'settled';
  }
  if (mayStillAppear) {
    return 'awaiting-task';
  }
  return 'not-run';
}

/**
 * How long to wait for a job to produce an OCR task before concluding it never
 * will (~2 min). A UI guard, not a load control: `checking` shows the reviewer
 * nothing about search, which is only defensible while brief. A task you can
 * SEE running is never abandoned.
 */
const MAX_POLLS_AWAITING_OCR_TASK = 12;

/** Carried between ticks by the shared poller (`sagas/engineStatus.ts`). */
export interface OcrWatchState {
  /** Still interested in further snapshots. */
  active: boolean;
  /** `processing` has been announced, so it is not announced twice. */
  sawTask: boolean;
  /** Ticks spent waiting for a task that has never appeared. */
  pollsAwaitingTask: number;
}

export const initialOcrWatchState = (): OcrWatchState => ({
  active: true,
  sawTask: false,
  pollsAwaitingTask: 0,
});

/** Dispatch the final state of a settled run. A failed run stays `failed` even with a manifest. */
function* reportSettledRun(
  tdoId: TDOId,
  manifestLoaded: boolean,
  failedOutright: boolean
) {
  if (manifestLoaded) {
    if (failedOutright) {
      yield* put(OCR_FAILED({ tdoId }));
    }
    return;
  }
  yield* put(failedOutright ? OCR_FAILED({ tdoId }) : OCR_NOT_RUN({ tdoId }));
}

/**
 * Settle a run over a file that is not OCR's output (VE-29505):
 *   - no asset resolved at all → say nothing (`indeterminate`);
 *   - primary changed → offer the reload;
 *   - primary unchanged, no `ocr` pages → load the manifest in place;
 *   - primary unchanged, `ocr` pages → report `failed` without the manifest,
 *     since its `ocr` pages would hide warnings on pages with no text.
 *
 * The primary reading is decisive because the engine promotes its searchable
 * copy to primary BEFORE its task reports complete. A task this watch sees
 * terminal has therefore already republished whatever it was going to, so an
 * unmoved primary means no new file exists — not that one is still coming.
 */
function* settleOverUnpinnedFile(
  state: OcrWatchState,
  {
    tdoId,
    engineId,
    openedMediaAssetId,
    failedOutright,
  }: {
    tdoId: TDOId;
    engineId: string;
    openedMediaAssetId: string | null;
    failedOutright: boolean;
  }
) {
  if (!openedMediaAssetId) {
    // The open's media lookup failed, so this session resolved no file at all
    // and is rendering no PDF. Nothing about search can be established from
    // here, and `not-run` would license "review this by eye" on that non-
    // evidence. Offering the reload would only re-run the lookup that just
    // failed and raise the dismiss-less banner again on every reload — the
    // loop VE-29505 exists to close, reached by the other route.
    yield* put(OCR_INDETERMINATE({ tdoId }));
    return { ...state, active: false };
  }

  if (yield* call(ocrPublishedNewFile, tdoId, openedMediaAssetId)) {
    yield* put(OCR_REFRESH_AVAILABLE({ tdoId }));
    return { ...state, active: false };
  }

  const manifest = yield* call(fetchOcrManifest, tdoId, engineId);
  if (manifest && manifestClaimsOcrText(manifest)) {
    console.warn(
      'OCR: manifest reports OCR text, but the searchable copy never became the primary media asset',
      { tdoId, openedMediaAssetId }
    );
    yield* put(OCR_FAILED({ tdoId }));
    return { ...state, active: false };
  }

  if (manifest) {
    yield* put(OCR_MANIFEST_LOADED({ tdoId, manifest }));
  }
  yield* reportSettledRun(tdoId, !!manifest, failedOutright);
  return { ...state, active: false };
}

/**
 * Advance `ocrEngineState` on one status reading; returns the next watch state.
 * Always leaves `checking`, so no OCR messaging waits on a spinner:
 *   - no task, no job                   → `not-run` (silent; pre-OCR docs)
 *   - no task, job still running        → keep watching, assert nothing
 *   - task in flight                    → `processing`
 *   - terminal task, manifest loads     → `done` / `failed`
 *   - complete, no readable manifest    → `not-run`, not `done`: nothing says
 *     pages were read.
 *
 * `pinnedOcrOutput` splits what a settled task means, and it is a FACT about the
 * file on screen rather than a fact about this watch: the manifest describes
 * OCR's searchable copy, so loading it and reporting `done` is only honest when
 * the document being rendered IS that copy. Where it is not, the reviewer is
 * OFFERED a reload — never a silent swap.
 *
 * This used to be `firstReading`, which stands in for the same thing and is
 * wrong twice. A run finishing between the open and the first tick leaves the
 * first reading terminal over the PRE-OCR file, which was then reported
 * searchable; and a re-run deliberately restarts the watch, so its "first"
 * reading is taken by a session that pinned its file a long time ago. Both said
 * `done` over a document with no text layer, with the manifest suppressing the
 * per-page notice that would have contradicted it.
 *
 * For an unpinned file, see `settleOverUnpinnedFile` (VE-29505).
 *
 * `firstReading` still decides whether a poll counts against the
 * wait-for-a-task budget, which is genuinely about this watch.
 */
export function* stepOcrWatch(
  state: OcrWatchState,
  status: EngineTaskStatus,
  {
    tdoId,
    engineId,
    firstReading,
    pinnedOcrOutput,
    openedMediaAssetId,
  }: {
    tdoId: TDOId;
    engineId: string;
    firstReading: boolean;
    pinnedOcrOutput: boolean;
    /** The `media` asset this session opened (`session.mediaAssetId`). */
    openedMediaAssetId: string | null;
  }
) {
  const disposition = classifyOcrStatus(status);

  if (disposition === 'stalled') {
    yield* put(OCR_TIMED_OUT({ tdoId }));
    return { ...state, active: false };
  }

  if (disposition === 'running') {
    if (!state.sawTask) {
      yield* put(OCR_PROCESSING({ tdoId }));
    }
    return { ...state, sawTask: true };
  }

  if (disposition === 'settled') {
    const failedOutright = status.hasFailed && !status.hasComplete;
    if (!pinnedOcrOutput) {
      return yield* settleOverUnpinnedFile(state, {
        tdoId,
        engineId,
        openedMediaAssetId,
        failedOutright,
      });
    }
    const loaded = yield* call(loadOcrManifest, tdoId, engineId);
    yield* reportSettledRun(tdoId, loaded, failedOutright);
    return { ...state, active: false };
  }

  if (disposition === 'awaiting-task') {
    // The first reading starts the wait rather than counting against it.
    const waited = firstReading
      ? state.pollsAwaitingTask
      : state.pollsAwaitingTask + 1;
    if (waited < MAX_POLLS_AWAITING_OCR_TASK) {
      // Assert nothing meanwhile.
      return { ...state, pollsAwaitingTask: waited };
    }
    // Giving up looking is not an answer: `indeterminate`, not `not-run`.
    yield* put(OCR_INDETERMINATE({ tdoId }));
    return { ...state, active: false, pollsAwaitingTask: waited };
  }

  // A job that settled without an OCR task was never an OCR job — a real answer.
  yield* put(OCR_NOT_RUN({ tdoId }));
  return { ...state, active: false };
}

/**
 * The status read failed. NOT `not-run` — a failed lookup is no evidence OCR
 * never ran, and `not-run` licenses "review this document by eye".
 * Deliberately unlike PII's fallback; see `stepPiiWatchError`.
 *
 * `tdoId` comes from the watch that took the reading, not from whatever session
 * is on screen when it lands.
 */
export function* stepOcrWatchError(tdoId: TDOId, e: unknown) {
  console.warn(
    'Document open: OCR status check failed; showing no OCR information.',
    e
  );
  yield* put(OCR_INDETERMINATE({ tdoId }));
}

/** No OCR engine configured: nothing ran, and nothing is coming. */
export function* resolveOcrUnconfigured(tdoId: TDOId) {
  yield* put(OCR_NOT_RUN({ tdoId }));
}

/**
 * The user took the reload: flush anything unsaved, then re-open. The re-open
 * resolves the new primary asset, rebuilds the session and re-runs the pre-pass
 * over the OCR'd text layer.
 *
 * A failed save aborts rather than trading the reviewer's edits for a text
 * layer; they keep the document they were working in and the offer stands.
 */
export function* handleReloadForOcr(action: ReturnType<typeof RELOAD_FOR_OCR>) {
  const session = yield* select(selectSession);
  if (!session) {
    return;
  }

  // Said BEFORE the flush, not after. Re-opening keeps the current document on
  // screen while it works — the session is only replaced on success — so with
  // an unsaved-work flush in front of it the click looked like it did nothing
  // for several seconds.
  yield* put(
    enqueueSnackbar({
      message: 'Reloading document…',
      variant: 'info',
      autoHideDuration: 4000,
    })
  );

  // `hasUnsavedWork`, not `status === 'dirty'`: a failed save leaves edits
  // pending under `error`, and re-opening rebuilds from the persisted asset.
  if (hasUnsavedWork(session.save)) {
    const requestId = `ocr-reload-${Date.now()}`;
    yield* put(SAVE_NOW({ requestId }));
    const { failure } = yield* race({
      success: take(
        (a: { type: string; payload?: { requestId?: string } }) =>
          a.type === SAVE_SUCCESS.type && a.payload?.requestId === requestId
      ),
      failure: take(
        (a: { type: string; payload?: { requestId?: string } }) =>
          a.type === SAVE_FAILURE.type && a.payload?.requestId === requestId
      ),
    });
    if (failure) {
      yield* put(
        enqueueSnackbar({
          message:
            'Could not save your changes, so the document was not reloaded.',
        })
      );
      // Nothing is going to re-open, so nothing is going to re-run the
      // lexicons — and the notice `RELOAD_FOR_OCR` raised would otherwise say
      // matches were updating for the rest of the session, next to a snackbar
      // saying the reload did not happen.
      yield* put(OCR_RELOAD_SETTLED());
      return;
    }
  }

  // The reducer starts `keywordSearch` empty, while the rail's input keeps the
  // term (same tdoId, no remount) — without the seed the reviewer is left with
  // their phrase on screen and no results.
  const { phrase, matchCase, wholeWord } = session.keywordSearch;
  // The page comes from the CONTROL, not the session: `currentPage` only moves
  // on explicit navigation, so a reviewer who scrolled here freely would be
  // returned to wherever they last clicked. The banner reads the live visible
  // page and sends it.
  const page = action?.payload?.page;
  yield* put(
    OPEN_DOCUMENT({
      tdoId: session.tdoId,
      ...(phrase.trim()
        ? { keywordSearchSeed: { phrase, matchCase, wholeWord } }
        : {}),
      // Seeded whenever there is anything worth carrying — the page, or a PII
      // result the reviewer already watched arrive.
      ...((page && page > 1) || session.piiEngineState === 'done'
        ? {
            viewSeed: {
              page: page ?? 1,
              zoom: session.zoom,
              ...(session.piiEngineState === 'done'
                ? { piiState: 'done' as const }
                : {}),
            },
          }
        : {}),
    })
  );
}

/**
 * RUN_OCR_DETECTION — start OCR on the document already open.
 *
 * Creates the job and stops. SUCCESS is what hands it to the shared
 * engine-status poller, which is already watching this document and finds the
 * new task on its next tick; polling from here would be a second watcher of the
 * same engine.
 */
export function* handleRunOcrDetection() {
  // Hoisted out of the try: the catch reverts the optimistic `processing`, and
  // that revert has to name the document the run was started on — by the time
  // a submission fails, the reviewer may be looking at another one.
  let tdoId: TDOId | null = null;
  try {
    const session = yield* select(selectSession);
    if (!session) {
      return;
    }
    tdoId = session.tdoId;
    const { documentOcrEngineId, defaultClusterId } =
      yield* select(selectConfigEngines);
    if (!documentOcrEngineId) {
      throw new Error('documentOcrEngineId is not configured');
    }

    const token = yield* select(selectToken);
    const { apiRoot, graphQLEndpoint, veritoneAppId } =
      yield* select(selectGraphQLConfig);

    const created = yield* call(
      serviceCreateDocumentCognitionJob,
      session.tdoId,
      { ocrEngineId: documentOcrEngineId },
      false,
      defaultClusterId ?? '',
      token,
      apiRoot,
      graphQLEndpoint,
      veritoneAppId
    );

    // Scoped to the document it was started on: the poller watching a DIFFERENT
    // document must ignore it. The task id rides along so the poller can watch
    // the new run by id rather than hunting the TDO's task list for it.
    yield* put(
      RUN_OCR_DETECTION_SUCCESS({
        tdoId: session.tdoId,
        jobId: created.jobId,
        ocrTaskId: created.taskIdByEngineId[documentOcrEngineId],
      })
    );
    yield* put(enqueueSnackbar({ message: 'OCR started.', variant: 'info' }));
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Failed to start OCR';
    if (tdoId) {
      yield* put(RUN_OCR_DETECTION_FAILURE({ tdoId, message }));
    }
    yield* put(
      enqueueSnackbar({
        message: `Could not start OCR: ${message}`,
        variant: 'error',
      })
    );
  }
}

export function* ocrStatusSaga() {
  // On-open resolution and polling live in the shared engine-status poller
  // (`sagas/engineStatus.ts`), which reads OCR and PII from one snapshot so the
  // two cannot observe the same job at different instants and disagree.
  yield* takeLatest(RELOAD_FOR_OCR, handleReloadForOcr);
  // takeLatest so a re-click supersedes an in-flight submission.
  yield* takeLatest(RUN_OCR_DETECTION, handleRunOcrDetection);
}
